import { yayApi } from '../baseApi'
import { LOAD_NUMBER } from 'config'
import { Thread, Post, PostCreated, PostCreate } from 'lib/_types'
import { axiosBaseQuery } from 'store/libs/baseQuery'
import { getDeviceId } from 'lib/user'
import {
  pathApiPostsMoveToThread,
  pathApiPostsMoveToThreadShow,
  pathApiThreads,
  pathApiThreadsJoinedStatuses,
  pathApiThreadsPosts,
  pathApiThreadsShow,
  pathApiThreadsShowMembersShow,
  pathSelfApiThreadsPosts,
} from 'lib/paths'

export const threadApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'Threads',
      'Thread/Base',
      'Thread/Posts',
      'Thread/JoinedStatuses',
      'Conversation/Base',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getThreads: build.query<
        {
          threads: Array<Thread>
          next_page_value: number
        },
        { groupId: string; from: string; joinStatus?: 'joined' | 'unjoined' }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'Threads', id: args?.from }]
        },
        query: ({ groupId, from, joinStatus }) => ({
          url: pathApiThreads,
          params: {
            number: LOAD_NUMBER,
            group_id: groupId,
            ...(joinStatus ? { join_status: joinStatus } : {}),
            ...(from?.length > 0 && from != '0' ? { from } : {}),
          },
        }),
        keepUnusedDataFor: 0, // We'll need a fresh `unread_count` for each thread every time.
      }),

      getThread: build.query<Thread, { threadId: string }>({
        providesTags: ['Thread/Base'],
        query: ({ threadId }) => ({
          url: pathApiThreadsShow({ threadId }),
          params: {},
        }),
      }),

      getThreadPosts: build.query<
        {
          next_page_value: string
          result: 'success'
          posts: Array<Post>
        },
        { threadId: string; from?: string }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'Thread/Posts', id: args?.from }]
        },
        query: ({ threadId, from }) => ({
          url: pathApiThreadsPosts({ threadId }),
          params: {
            number: LOAD_NUMBER,
            ...(from?.length > 0 && from != '0' ? { from } : {}),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      createThread: build.mutation<
        any,
        {
          groupId: string
          title: string
          threadIconFilename: null | string
        }
      >({
        query: ({ groupId, title, threadIconFilename }) => ({
          url: pathApiThreads,
          method: 'POST',
          data: {
            group_id: groupId,
            title,
            thread_icon_filename: threadIconFilename,
          },
        }),
      }),

      updateThread: build.mutation<
        any,
        {
          title?: string
          threadIconFilename?: string
          threadId: number
        }
      >({
        invalidatesTags: ['Thread/Base'],
        query: ({ title, threadId, threadIconFilename }) => ({
          url: pathApiThreadsShow({ threadId }),
          method: 'PUT',
          data: {
            title,
            thread_icon_filename: threadIconFilename,
          },
        }),
      }),

      movePostToThread: build.mutation<
        any,
        {
          postId: number
          title?: string
          threadIconFilename?: string
        }
      >({
        query: ({ postId, title, threadIconFilename }) => ({
          url: pathApiPostsMoveToThread({ postId }),
          method: 'POST',
          data: {
            title,
            thread_icon_filename: threadIconFilename,
          },
        }),
      }),

      addPostToThread: build.mutation<
        any,
        {
          postId: string
          threadId: number
        }
      >({
        query: ({ postId, threadId }) => ({
          url: pathApiPostsMoveToThreadShow({ postId, threadId }),
          method: 'PUT',
        }),
      }),

      createThreadPost: build.mutation<
        PostCreated,
        { threadId: string | number; data: PostCreate }
      >({
        invalidatesTags: ['Conversation/Base'],
        queryFn: async ({ threadId, data }, api) => {
          data.uuid = getDeviceId()

          return await axiosBaseQuery({ client: 'self' })(
            {
              url: pathSelfApiThreadsPosts({ threadId }),
              method: 'POST',
              data,
            },
            api
          )
        },
      }),

      deleteThread: build.mutation<any, { threadId: string }>({
        query: ({ threadId }) => ({
          url: pathApiThreadsShow({ threadId }),
          method: 'DELETE',
        }),
      }),

      joinThread: build.mutation<
        {
          response: any
          meta: any
          args: {
            thread: Thread
            userId: string
          }
        },
        { thread: Thread; userId: string }
      >({
        invalidatesTags: ['Thread/Base'],
        query: ({ thread, userId }) => ({
          url: pathApiThreadsShowMembersShow({ threadId: thread?.id, userId }),
          method: 'POST',
        }),
        transformResponse: (response, meta, args) => {
          return {
            response,
            meta,
            args,
          }
        },
      }),

      leaveThread: build.mutation<any, { threadId: string; userId: string }>({
        invalidatesTags: ['Thread/Base'],
        query: ({ threadId, userId }) => ({
          url: pathApiThreadsShowMembersShow({ threadId, userId }),
          method: 'DELETE',
        }),
      }),

      getThreadsJoinedStatuses: build.query<
        {
          [key: string]: 'joined' | 'unjoined'
        },
        { ids: Array<number> }
      >({
        providesTags: ['Thread/JoinedStatuses'],
        query: ({ ids }) => ({
          url: pathApiThreadsJoinedStatuses,
          params: {
            ids,
          },
        }),
        keepUnusedDataFor: 0,
      }),
    }),
    overrideExisting: false,
  })

export const {
  endpoints,
  useGetThreadsQuery,
  useGetThreadQuery,
  useGetThreadPostsQuery,
  useCreateThreadMutation,
  useUpdateThreadMutation,
  useMovePostToThreadMutation,
  useAddPostToThreadMutation,
  useCreateThreadPostMutation,
  useDeleteThreadMutation,
  useJoinThreadMutation,
  useLeaveThreadMutation,
  useGetThreadsJoinedStatusesQuery,
} = threadApi
